// src/components/shared/SendButtons.jsx
// Botones "Email" y "WhatsApp" con modales. Reusable para PO/GRN/Importacion — Sprint 28.

import { useState } from 'react';
import apiClient from '../../api/apiClient';

export default function SendButtons({
  endpointBase,
  tipoLabel = 'documento',
  defaultEmail = '',
  defaultTelefono = '',
}) {
  // endpointBase ejemplo: '/po/5' o '/grn/3' o '/importacion-usa/1'
  const [openEmail, setOpenEmail] = useState(false);
  const [openWa, setOpenWa] = useState(false);
  const [email, setEmail] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [telefono, setTelefono] = useState('');
  const [mensajeWa, setMensajeWa] = useState('');
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [toast, setToast] = useState(null);
  // Idioma para IA: 'en' por defecto porque muchos proveedores son en ingles
  const [idioma, setIdioma] = useState('en');

  // Auto-rellenar al abrir modales
  const openEmailModal = () => {
    if (defaultEmail && !email) setEmail(defaultEmail);
    setOpenEmail(true);
  };
  const openWaModal = () => {
    if (defaultTelefono && !telefono) setTelefono(defaultTelefono);
    setOpenWa(true);
  };

  const draftWithAI = async (canal) => {
    setDrafting(true);
    try {
      const r = await apiClient.post(`${endpointBase}/ai-draft`, { canal, idioma });
      const text = r?.data?.text || '';
      if (canal === 'email') setMensaje(text);
      else setMensajeWa(text);
    } catch (e) {
      setToast({ msg: e?.data?.message || 'IA no disponible', ok: false });
      setTimeout(() => setToast(null), 3000);
    } finally {
      setDrafting(false);
    }
  };

  // Selector de idioma compacto
  const IdiomaSelector = () => (
    <select value={idioma} onChange={(e) => setIdioma(e.target.value)}
      className="text-xs border rounded px-1 py-0.5 mr-1">
      <option value="en">🇺🇸 EN</option>
      <option value="es">🇨🇴 ES</option>
    </select>
  );

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  };

  const sendEmail = async () => {
    if (!email.trim()) { showToast('Pon un destinatario', false); return; }
    setSending(true);
    try {
      await apiClient.post(`${endpointBase}/email`, { destinatario: email.trim(), mensaje });
      showToast('Email enviado correctamente ✓');
      setOpenEmail(false); setEmail(''); setMensaje('');
    } catch (e) {
      showToast(e?.data?.message || e?.message || 'Error al enviar', false);
    } finally {
      setSending(false);
    }
  };

  const sendWhatsApp = async () => {
    setSending(true);
    try {
      const r = await apiClient.post(`${endpointBase}/whatsapp`, {
        telefono: telefono.trim(),
        mensaje: mensajeWa,
      });
      const data = r?.data || {};
      if (data.mode === 'wa_me' && data.url) {
        window.open(data.url, '_blank');
        showToast('WhatsApp Web abierto');
        setOpenWa(false); setTelefono(''); setMensajeWa('');
      } else if (data.mode === 'twilio' && data.success) {
        showToast('WhatsApp enviado via Twilio ✓');
        setOpenWa(false); setTelefono(''); setMensajeWa('');
      } else if (data.mode === 'twilio' && !data.success) {
        // Twilio respondio con error
        const err = data?.detail?.error || 'Twilio rechazo el mensaje';
        showToast('Twilio error: ' + err, false);
      } else {
        showToast('OK');
        setOpenWa(false); setTelefono(''); setMensajeWa('');
      }
    } catch (e) {
      showToast(e?.data?.message || e?.message || 'Error', false);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button type="button" onClick={openEmailModal}
        className="text-sm px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700">
        ✉️ Email
      </button>
      <button type="button" onClick={openWaModal}
        className="text-sm px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700">
        💬 WhatsApp
      </button>

      {toast && (
        <div className={`fixed bottom-6 right-6 p-3 rounded shadow-lg text-sm z-50 ${
          toast.ok ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.msg}
        </div>
      )}

      {/* Modal Email */}
      {openEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
             onClick={() => setOpenEmail(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-5">
              <h3 className="text-lg font-semibold mb-4">Enviar {tipoLabel} por Email</h3>
              <div className="mb-3">
                <label className="block text-sm font-medium mb-1">Destinatario</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm" placeholder="alguien@email.com" required/>
              </div>
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium">Mensaje (opcional)</label>
                  <div className="flex items-center gap-1">
                    <IdiomaSelector />
                    <button type="button" onClick={() => draftWithAI('email')} disabled={drafting}
                      className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded hover:bg-purple-200 disabled:opacity-50">
                      {drafting ? 'Redactando...' : '🪄 Redactar con IA'}
                    </button>
                  </div>
                </div>
                <textarea rows={6} value={mensaje} onChange={(e) => setMensaje(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm"
                  placeholder="Deja en blanco o pulsa 'Redactar con IA'"/>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setOpenEmail(false)}
                  className="text-sm px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Cancelar</button>
                <button onClick={sendEmail} disabled={sending}
                  className="text-sm px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">
                  {sending ? 'Enviando...' : 'Enviar'}
                </button>
              </div>
              <p className="text-[11px] text-gray-500 mt-3">
                Se adjunta automaticamente el PDF del {tipoLabel}. Si falla: verifica SMTP en .env.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Modal WhatsApp */}
      {openWa && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
             onClick={() => setOpenWa(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-5">
              <h3 className="text-lg font-semibold mb-4">Compartir {tipoLabel} por WhatsApp</h3>
              <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded text-xs text-green-800">
                💡 Al pulsar <b>"Abrir WhatsApp"</b> se abrira WhatsApp Web con el mensaje ya escrito.
                Solo pulsa <b>Enviar</b> dentro de WhatsApp para completar.
              </div>
              <div className="mb-3">
                <label className="block text-sm font-medium mb-1">Telefono (opcional)</label>
                <input type="tel" value={telefono} onChange={(e) => setTelefono(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm" placeholder="+573001234567"/>
                <p className="text-[11px] text-gray-500 mt-1">
                  Si lo dejas vacio, WhatsApp te dejara elegir a quien enviar.
                </p>
              </div>
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium">Mensaje (opcional)</label>
                  <div className="flex items-center gap-1">
                    <IdiomaSelector />
                    <button type="button" onClick={() => draftWithAI('whatsapp')} disabled={drafting}
                      className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded hover:bg-purple-200 disabled:opacity-50">
                      {drafting ? 'Redactando...' : '🪄 Redactar con IA'}
                    </button>
                  </div>
                </div>
                <textarea rows={5} value={mensajeWa} onChange={(e) => setMensajeWa(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm"
                  placeholder="Deja en blanco o pulsa 'Redactar con IA'"/>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setOpenWa(false)}
                  className="text-sm px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Cancelar</button>
                <button onClick={sendWhatsApp} disabled={sending}
                  className="text-sm px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">
                  {sending ? 'Abriendo...' : '📱 Abrir WhatsApp'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
